/**
 * Page manager screen watchers
 * @module services/pm-screen/watchers
 */
import { Map, List } from 'immutable'
import { isSynced as resolverIsSynced } from 'services/resolver/synced'
import { SET_RESOLVER_DATA } from 'services/resolver/actions'
import { getUserLanguagePrimary } from 'services/user'
import { BOOTSTRAP_PHASE_COMPLETE } from 'services/app'
import { SET_APP_BOOTSTRAP_PHASE } from 'services/app/actions'
import { SET_USER_DATA_LANGUAGE } from 'services/user/actions'
import { SET_AUTH_LOGIN_SUCCESS } from 'services/auth/actions'
import { deleteMultiplePMSections as deleteMultiplePMSectionsAction } from 'services/pm-section/actions'
import { PM_SCREEN_TYPE_MEMBER_HOME, PM_SCREEN_TYPE_YOGA_HOME, PM_SCREEN_TYPE_CENTERS } from './types'
import { GET_PM_SCREEN, getPmScreen as getPmScreenAction, deletePmScreen as deletePmScreenAction } from './actions'
import { getPmScreen } from './watchers-helpers'
import { getPmScreenData, getPmScreenTypeFromResolver } from '.'

const PM_SCREEN_TYPES = List([
  PM_SCREEN_TYPE_MEMBER_HOME, PM_SCREEN_TYPE_YOGA_HOME, PM_SCREEN_TYPE_CENTERS,
])

/**
 * Load a page manager screen by path
 */
export function onPmScreenShouldLoad ({ after }) {
  return after(
    [SET_RESOLVER_DATA, SET_APP_BOOTSTRAP_PHASE, SET_USER_DATA_LANGUAGE, SET_AUTH_LOGIN_SUCCESS],
    async ({ dispatch, state }) => {
      const { app = Map(), auth = Map(), user = Map(), resolver = Map() } = state
      const language = getUserLanguagePrimary(user)
      const resolverSynced = resolverIsSynced(resolver, location)
      const resolverProcessing = resolver.get('processing')
      const bootstrapComplete = app.get('bootstrapPhase') === BOOTSTRAP_PHASE_COMPLETE
      if (!bootstrapComplete || resolverProcessing || !resolverSynced) {
        return
      }
      const pmScreenType = getPmScreenTypeFromResolver({ resolver, auth })
      if (pmScreenType) {
        const sectionsLimit = pmScreenType === PM_SCREEN_TYPE_MEMBER_HOME ? 3 : undefined
        dispatch(getPmScreenAction({ screenType: pmScreenType, language, sectionsLimit }))
      }
    },
  )
}

/**
 * Reset a page manager screen when you are not on the path
 */
export function onPmScreenShouldUnload ({ after }) {
  return after(SET_RESOLVER_DATA, async ({ dispatch, state }) => {
    const { pmScreen = Map(), auth = Map(), user = Map(), resolver = Map() } = state
    const language = getUserLanguagePrimary(user)
    const pmScreenType = getPmScreenTypeFromResolver({ resolver, auth })
    PM_SCREEN_TYPES.filter((screenType) => {
      const data = getPmScreenData({ pmScreen, screenType, language })
      return screenType !== pmScreenType && data
    }).forEach((screenType) => {
      const data = getPmScreenData({ pmScreen, screenType, language }) || Map()
      // Delete all language versions of screen
      dispatch(deletePmScreenAction(screenType))
      const sectionIds = data.get('sectionIds') || List()
      if (!sectionIds.isEmpty()) {
        dispatch(deleteMultiplePMSectionsAction(sectionIds))
      }
    })
  })
}

/**
 * Handle get page manager screen action
 */
export function onGetPmScreen ({ after }) {
  return after(GET_PM_SCREEN, async ({ dispatch, state, action }) => {
    const { payload: { screenType, language, sectionsLimit } = {} } = action
    const { auth = Map(), pmScreen = Map() } = state
    await getPmScreen({
      dispatch,
      auth,
      pmScreen,
      screenType,
      language,
      sectionsLimit,
    })
  })
}
